import { useEffect, useRef, useState } from "react";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import { formatUserPhone, getUserInitials } from "@/modules/users";
import type { User } from "@/modules/users";
import styles from "./toggle-user-status-dialog.module.scss";

export const DEACTIVATE_TITLE = "Desativar usuário";
export const ACTIVATE_TITLE = "Ativar usuário";

export const DEACTIVATE_SUBTITLE = "Essa ação desativa o acesso do usuário ao Hub.";
export const ACTIVATE_SUBTITLE = "Essa ação ativa o acesso do usuário ao Hub.";

/** MS001 — alerta do impacto da inativação, apresentado antes da confirmação. */
export const DEACTIVATE_MESSAGE =
  "Este usuário perderá o acesso ao Hub e suas pendências não serão tratadas. O gerente deve reatribuir as responsabilidades dele a outro usuário.";

/** MS002 — informativo do efeito da ativação, apresentado antes da confirmação. */
export const ACTIVATE_MESSAGE =
  "O acesso do usuário será restaurado e ele poderá acessar novamente o Hub e gerenciar suas pedências e responsabilidades.";

export const DEACTIVATE_CONFIRM_LABEL = "Desativar Usuário";
export const ACTIVATE_CONFIRM_LABEL = "Ativar Usuário";

export const USER_INFO_TITLE = "Informações do Usuário";

const EMPTY_VALUE = "—";

/** Tempo que o ícone de copiar permanece em "copiado" antes de voltar ao normal. */
const COPY_FEEDBACK_MS = 2000;

interface ToggleUserStatusDialogProps {
  user: User;
  /**
   * Efetiva a alteração de status. Cabe ao chamador comunicar o resultado e
   * fechar o modal — numa falha de gravação o modal segue aberto para nova
   * tentativa. A promessa não deve ser rejeitada.
   */
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Confirmação da ativação/inativação (RN04). O modal mostra a identificação do
 * usuário somente para consulta — não há campo editável — e o impacto da
 * operação, oferecendo apenas confirmar e cancelar.
 */
export function ToggleUserStatusDialog({ user, onConfirm, onCancel }: ToggleUserStatusDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const deactivating = user.status === "ativo";
  const telefone = formatUserPhone(user.celular);

  useEffect(() => {
    return () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  async function handleConfirm() {
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  }

  /** Cópia do e-mail prevista no protótipo; sem suporte do navegador, nada muda. */
  async function handleCopyEmail() {
    try {
      await navigator.clipboard?.writeText(user.email);
    } catch {
      return;
    }

    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
  }

  const header = (
    <div className={styles.header}>
      <span
        className={`${styles.headerIcon} ${deactivating ? styles.headerIconDeactivate : styles.headerIconActivate}`}
        aria-hidden
      >
        <i className="pi pi-power-off" />
      </span>

      <span className={styles.headerText}>
        <span className={styles.headerTitle}>
          {deactivating ? DEACTIVATE_TITLE : ACTIVATE_TITLE}
        </span>
        <span className={styles.headerSubtitle}>
          {deactivating ? DEACTIVATE_SUBTITLE : ACTIVATE_SUBTITLE}
        </span>
      </span>
    </div>
  );

  const footer = (
    <div className={styles.actions} data-testid="toggle-user-status-actions">
      <Button
        type="button"
        className={styles.cancelButton}
        label="Cancelar"
        severity="secondary"
        text
        onClick={onCancel}
        disabled={isSubmitting}
        data-testid="toggle-user-status-cancel"
      />
      <Button
        type="button"
        className={deactivating ? styles.deactivateButton : styles.activateButton}
        label={deactivating ? DEACTIVATE_CONFIRM_LABEL : ACTIVATE_CONFIRM_LABEL}
        loading={isSubmitting}
        onClick={handleConfirm}
        data-testid="toggle-user-status-confirm"
      />
    </div>
  );

  const avatar = user.fotoUrl ? (
    <Avatar
      className={styles.avatar}
      image={user.fotoUrl}
      imageAlt={`Foto de ${user.nome}`}
      shape="circle"
    />
  ) : (
    <Avatar
      className={styles.avatar}
      label={getUserInitials(user.nome)}
      shape="circle"
      aria-label={`Sem foto de ${user.nome}`}
    />
  );

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      header={header}
      footer={footer}
      visible
      modal
      draggable={false}
      resizable={false}
      ariaCloseIconLabel="Fechar"
      onHide={onCancel}
      breakpoints={{ "40rem": "95vw" }}
      data-testid="toggle-user-status-dialog"
    >
      <div className={styles.body}>
        <section
          className={styles.card}
          aria-labelledby="toggle-user-status-info-title"
          data-testid="toggle-user-status-details"
        >
          <h3 id="toggle-user-status-info-title" className={styles.cardTitle}>
            {USER_INFO_TITLE}
          </h3>

          <div className={styles.cardBody}>
            {avatar}

            <div className={styles.cardText}>
              <p className={styles.name} data-testid="toggle-user-status-name">
                {user.nome}
              </p>

              <div className={styles.contacts}>
                <p className={styles.contact} data-testid="toggle-user-status-email">
                  <i className={`pi pi-envelope ${styles.contactIcon}`} aria-hidden />
                  <span className={styles.contactValue}>{user.email || EMPTY_VALUE}</span>
                  {user.email ? (
                    <button
                      type="button"
                      className={styles.copyButton}
                      onClick={handleCopyEmail}
                      aria-label={copied ? "E-mail copiado" : `Copiar e-mail de ${user.nome}`}
                      data-testid="toggle-user-status-copy-email"
                    >
                      <i className={copied ? "pi pi-check" : "pi pi-copy"} aria-hidden />
                    </button>
                  ) : null}
                </p>

                <p className={styles.contact} data-testid="toggle-user-status-phone">
                  <i className={`pi pi-phone ${styles.contactIcon}`} aria-hidden />
                  <span className={styles.contactValue}>{telefone || EMPTY_VALUE}</span>
                </p>

                <p className={styles.contact} data-testid="toggle-user-status-team">
                  <i className={`pi pi-users ${styles.contactIcon}`} aria-hidden />
                  <span className={styles.contactValue}>{user.time || EMPTY_VALUE}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        <p
          className={`${styles.message} ${deactivating ? styles.alertMessage : styles.infoMessage}`}
          role={deactivating ? "alert" : "status"}
          data-testid="toggle-user-status-message"
        >
          <i className={`pi pi-exclamation-triangle ${styles.messageIcon}`} aria-hidden />
          {deactivating ? DEACTIVATE_MESSAGE : ACTIVATE_MESSAGE}
        </p>
      </div>
    </Dialog>
  );
}
